/**
 * Term matching for the prose highlighter — the frontend's mirror of the Python
 * matcher (`normalize._fold` / `_tokenize`, `search.py`).
 *
 * Its own module so it can be unit-tested: `HighlightedResponse.tsx` pulls in
 * `services/api`, and the vitest environment is `node`.
 *
 * ⚠ Lock-step, same rule as `targetColors.isPairSatisfied`: a highlight is a
 * user-visible claim about a verdict computed on the Python side, so widening
 * anything here without widening `_tokenize` paints text the backend never
 * matched.
 */

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Apostrophe variants → the ASCII one. Single code points, so length is preserved. */
function foldApostrophes(s: string): string {
  return s.replace(/[‘’ʼʹ′´`＇]/g, "'");
}

/**
 * Separator between two tokens of a term: any run of non-alphanumerics.
 *
 * The matcher DROPS punctuation-only tokens and bridges the gap, so the marque
 * compiled from "Jacy'z Hotel & Resort" reaches us as the three-token term
 * `jacy'z hotel resort`. Matching that as a literal substring would never paint
 * the very text it was compiled from — the '&' is still on screen.
 * `\p{L}\p{N}` mirrors Python's `str.isalnum()`, the same mirror
 * `targetColors.endsSentenceWithin` makes.
 *
 * Deliberately no more permissive than `_tokenize`: it bridges punctuation and
 * whitespace but never another WORD, so the matcher's contiguity still holds.
 */
const TERM_SEPARATOR = '[^\\p{L}\\p{N}]+';

export function termToPattern(foldedTerm: string): string {
  return foldedTerm.split(' ').filter(Boolean).map(escapeRegex).join(TERM_SEPARATOR);
}

/**
 * Accent-fold a string while preserving its UTF-16 length so match offsets
 * still align to the ORIGINAL text. Per code point: NFD-decompose, drop
 * combining marks, and only substitute when the result has the same length
 * (so a precomposed `é` → `e`, but emoji / lone combining marks are left
 * untouched). Without this, a brand stored as `previfrance` never matches
 * `Prévifrance` in an engine response — `gi` case-folds but not accents.
 * Mirrors the backend `_fold_accents` in scoring.py.
 *
 * Apostrophes fold to the ASCII one for the same reason and in the same step:
 * a name typed `Jacy'z` (U+0027) must match the `Jacy’z` (U+2019) that ChatGPT
 * and Perplexity write. All the variants are single code points, so the 1:1
 * length promise this function makes still holds.
 */
export function foldAccentsPreserveLen(s: string): string {
  let out = '';
  for (const ch of s) {
    const f = foldApostrophes(ch).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    out += f.length === ch.length ? f : ch;
  }
  return out;
}
